const MINI_APP_LINK = import.meta.env.VITE_MINI_APP_LINK as string | undefined;
const BOT_USERNAME = import.meta.env.VITE_BOT_USERNAME as string | undefined;

const JOIN_CODE = /^[A-Za-z0-9_-]{8,32}$/;

/**
 * Builds the best available invite link:
 *  1. Mini App direct link (opens the app immediately, start_param=join_<code>)
 *  2. Bot deep link (/start join_<code> → bot replies with an "Open" button)
 *  3. Plain web URL, only in local development (outside Telegram the web
 *     page can't sign anyone in, so production must never hand this out)
 */
export function buildInviteLink(
  code: string,
  options: { miniAppLink?: string; botUsername?: string; origin: string },
): string {
  const param = `join_${code}`;
  if (options.miniAppLink) return `${options.miniAppLink.replace(/\/$/, '')}?startapp=${param}`;
  if (options.botUsername) return `https://t.me/${options.botUsername}?start=${param}`;
  return `${options.origin}/join/${code}`;
}

export const inviteLink = (code: string): string =>
  buildInviteLink(code, { miniAppLink: MINI_APP_LINK, botUsername: BOT_USERNAME, origin: window.location.origin });

/**
 * Where to send someone who opened the app's web address in a normal
 * browser (e.g. an old invite link): into Telegram, keeping the join code.
 * With the bot's Main Mini App set up, straight into the app.
 */
export function telegramLinkFor(
  pathname: string,
  options: { botUsername?: string; miniAppLink?: string },
): string | null {
  const code = /^\/join\/([^/]+)\/?$/.exec(pathname)?.[1];
  const join = code && JOIN_CODE.test(code) ? `join_${code}` : null;
  if (options.miniAppLink) {
    return `${options.miniAppLink.replace(/\/$/, '')}?startapp${join ? `=${join}` : ''}`;
  }
  if (!options.botUsername) return null;
  return join ? `https://t.me/${options.botUsername}?start=${join}` : `https://t.me/${options.botUsername}`;
}

/** Maps a launch start parameter to an in-app route. */
export function routeForStartParam(param: string | null | undefined): string | null {
  if (!param) return null;
  const join = /^join_([A-Za-z0-9_-]{8,32})$/.exec(param);
  if (join) return `/join/${join[1]}`;
  if (param === 'add_expense') return '/add';
  const group = /^group_([0-9a-f-]{36})$/i.exec(param);
  if (group) return `/groups/${group[1]}`;
  return null;
}
